import { useState, useEffect, useCallback } from 'react';
import { isGrpcRuntimeAvailable } from '../grpc/transport';
import {
  getAvailableValidationsGrpc,
  executeValidationGrpc,
  getLastReportGrpc,
  getDocumentInfoGrpc,
  highlightElementsGrpc,
  exportReportJsonGrpc,
  exportReportHtmlGrpc,
  applyQuickFixGrpc,
  getQuickFixResultGrpc,
  reconnectElementsGrpc,
  reconnectAllPairsGrpc,
  getReconnectionResultGrpc,
} from '../grpc/validationGrpcClient';
import {
  getMappingsGrpc,
  getAllParameterNamesGrpc,
  validateMappingsGrpc,
  saveMappingsGrpc,
  resetMappingsToDefaultsGrpc,
  exportMappingsGrpc,
  importMappingsGrpc,
} from '../grpc/pcfMappingClient';

const READINESS_RETRY_MS = 1_000;

async function callGrpcFallback(methodPath: string, params?: Record<string, any>): Promise<any> {
  switch (methodPath) {
    // ── Validation domain ─────────────────────────────────────────────────
    case 'validation.getAvailableValidations':
      return getAvailableValidationsGrpc();
    case 'validation.getDocumentInfo':
      return getDocumentInfoGrpc();
    case 'validation.getLastReport':
      return getLastReportGrpc();
    case 'validation.executeValidation': {
      const configJson = params?.config ?? params?.configJson ?? '{}';
      return executeValidationGrpc(configJson, { onProgress: params?.onProgress });
    }
    case 'validation.highlightElements': {
      const ids = (params?.elementIds ?? []).map((id: number | string) => BigInt(id));
      return highlightElementsGrpc(ids);
    }
    case 'validation.exportToJson':
      return exportReportJsonGrpc();
    case 'validation.exportToHtml':
      return exportReportHtmlGrpc();
    case 'validation.applyQuickFix': {
      const fixDataJson = params?.fixDataJson ?? JSON.stringify(params ?? {});
      return applyQuickFixGrpc(fixDataJson, { onProgress: params?.onProgress });
    }
    case 'validation.getQuickFixResult':
      return getQuickFixResultGrpc();
    case 'validation.reconnectElements': {
      const id1 = BigInt(params?.elementId1 ?? 0);
      const id2 = BigInt(params?.elementId2 ?? 0);
      return reconnectElementsGrpc(id1, id2, { onProgress: params?.onProgress });
    }
    case 'validation.reconnectAllPairs': {
      const validationId = params?.validationId ?? 0;
      return reconnectAllPairsGrpc(validationId, { onProgress: params?.onProgress });
    }
    case 'validation.getReconnectionResult':
      return getReconnectionResultGrpc();

    // ── PCF Mapping domain ────────────────────────────────────────────────
    case 'pcfMapping.getAll': {
      const response = await getMappingsGrpc();
      if (!response.success || !response.configJson) {
        return null;
      }
      const config: unknown = JSON.parse(response.configJson);
      return config;
    }
    case 'pcfMapping.getAllParameterNames':
      return getAllParameterNamesGrpc({ onProgress: params?.onProgress });
    case 'pcfMapping.validate':
      return validateMappingsGrpc({ onProgress: params?.onProgress });
    case 'pcfMapping.save': {
      const configJson = params?.mappings ?? params?.configJson ?? JSON.stringify(params ?? {});
      return saveMappingsGrpc(configJson, { onProgress: params?.onProgress });
    }
    case 'pcfMapping.resetToDefaults':
      return resetMappingsToDefaultsGrpc({ onProgress: params?.onProgress });
    case 'pcfMapping.export':
      return exportMappingsGrpc();
    case 'pcfMapping.import': {
      const json = params?.json ?? JSON.stringify(params ?? {});
      return importMappingsGrpc(json, { onProgress: params?.onProgress });
    }

    default:
      return {
        success: false,
        error: `${methodPath} is not available in current runtime`,
      };
  }
}

/**
 * Custom hook to interact with Revit API via WebView2 bridge
 *
 * This hook provides a clean abstraction layer for calling Revit methods
 * from React components. It handles the WebView2 bridge initialization
 * and provides a simple call() method for invoking Revit operations.
 *
 * @example
 * const { isReady, call } = useRevitApi();
 *
 * // Call a validation method
 * const result = await call('validation.executeValidation', { config: configJson });
 */
export function useRevitApi() {
  const [isReady, setIsReady] = useState(() => isGrpcRuntimeAvailable());

  useEffect(() => {
    if (isGrpcRuntimeAvailable()) {
      setIsReady(true);
      return undefined;
    }

    // The gRPC runtime is the only transport. The grpcPort may not be resolvable
    // on the very first tick (e.g. before the ?grpcPort query param is parsed), so
    // re-check on a short timer until it appears.
    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const check = (): void => {
      if (disposed) {
        return;
      }
      if (isGrpcRuntimeAvailable()) {
        setIsReady(true);
        return;
      }
      retryTimer = setTimeout(check, READINESS_RETRY_MS);
    };

    retryTimer = setTimeout(check, READINESS_RETRY_MS);

    return () => {
      disposed = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, []);

  /**
   * Call a method on the Revit bridge
   *
   * Method names use dot notation for namespacing:
   * - 'validation.executeValidation' -> validates PCF data
   * - 'validation.getAvailableValidations' -> gets validation list
   * - 'pcfMapping.getAll' -> gets PCF parameter mappings
   *
   * @param methodPath - The method path (e.g., 'validation.executeValidation')
   * @param params - Optional parameters object to pass to the method
   * @returns Promise resolving to the method result
   */
  const call = useCallback(async (methodPath: string, params?: Record<string, any>) => {
    if (!isReady) {
      throw new Error('Revit bridge is not ready');
    }

    try {
      // ALL Web UI <-> Revit communication goes through gRPC. The legacy WebView2
      // COM host bridge has been removed entirely (product decision: WebView2 is
      // used ONLY to open the Web UI, never as a transport — no COM, no postMessage).
      // The COM bridge no longer implemented the validation/mapping methods, and
      // accessing a missing host-object member threw "Element not found (0x80070490)",
      // which is what wedged the Validation page while QuickFill (already gRPC) worked.
      return await callGrpcFallback(methodPath, params);
    } catch (error) {
      console.error(`Error calling Revit method ${methodPath}:`, error);
      throw error;
    }
  }, [isReady]);

  return {
    isReady,
    call,
  };
}
